import {Component, HostListener, Input, OnInit} from '@angular/core';
import {DurationFormUnit, DurationInputOptions} from "@shared/components/duration-input/duration-input.component";
import {NgbActiveModal, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap/modal/modal-ref";
import {Duration} from "moment-timezone";
import {Observable} from 'rxjs';
import {finalize} from "rxjs/operators";
import {FormControl} from "@angular/forms";


export interface DurationInputModalOptions extends DurationInputOptions {
  duration: Duration | null;
  saveFn: (duration: Duration | null) => Observable<any>;
}


@Component({
  selector: 'app-duration-input-modal',
  templateUrl: './duration-input-modal.component.html',
  styleUrl: './duration-input-modal.component.scss',
  standalone: false
})
export class DurationInputModalComponent implements DurationInputModalOptions, OnInit {

  @Input() showLabel: boolean;
  @Input() clearable: boolean;
  @Input() units: DurationFormUnit[];
  @Input() duration: Duration | null;
  @Input() saveFn: (duration: Duration | null) => Observable<any>;

  protected saving = false;
  protected durationControl: FormControl<Duration>;

  constructor(protected activeModal: NgbActiveModal) {
  }

  ngOnInit(): void {
    this.durationControl = new FormControl<Duration | null>(this.duration);
  }

  static openInModal(modalService: NgbModal, options: DurationInputModalOptions): NgbModalRef {
    if (!options.saveFn) {
      throw new Error('"saveFn" must be set for ' + DurationInputModalComponent.name);
    }
    const modalRef = modalService.open(DurationInputModalComponent, {
      centered: true,
      keyboard: false,
      size: 'sm',
      backdrop: 'static'
    });
    const modalComponent: DurationInputModalComponent = modalRef.componentInstance;
    modalComponent.clearable = options.clearable ?? true;
    modalComponent.showLabel = options.showLabel ?? true;
    modalComponent.units = options.units ?? ['year', 'month', 'day', 'hour', 'minute'];
    modalComponent.duration = options.duration ?? null;
    modalComponent.saveFn = options.saveFn;
    return modalRef;
  }

  save() {
    this.saving = true;
    this.saveFn(this.durationControl.value).pipe(finalize(() => this.saving = false)).subscribe(result => this.activeModal.close(result));
  }

  @HostListener('window:keyup.escape', ['$event'])
  keyEvent(_event: KeyboardEvent) {
    // We disable keyboard tracking for this modal as when editing a ticket this is a nested modal (above the Ticket View Modal),
    // and we want to ensure that the ESC key only closes this (outer) modal and not the Ticket View Modal. To do that we need to
    // close this modal manually, as otherwise the modal event is sent before the key event is received by the View Ticket Modal
    // and the View Ticket modal is not able to determine if another modal (i.e. this one) is open
    this.activeModal.dismiss('escape key pressed')
  }
}
